// frontend/src/pages/AdminLogsPage.js
import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { format } from 'date-fns';
import PageHeader from '../components/ui/PageHeader';
import useBreakpoint from '../hooks/useBreakpoint';
import BottomSheet from '../components/mobile/BottomSheet';
import BotonFiltros from '../components/mobile/BotonFiltros';
import ChipsFiltros from '../components/mobile/ChipsFiltros';
import { coincideBusqueda } from '../utils/textoComparable';

const AdminLogsPage = () => {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtros
    const [filterAccion, setFilterAccion] = useState('');
    const [filterUsuario, setFilterUsuario] = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                setLoading(true);
                const response = await adminService.getLogs();
                // Ordenar por fecha descendente (lo más nuevo primero) si no viene ordenado
                const sortedLogs = response.data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
                setLogs(sortedLogs);
                setFilteredLogs(sortedLogs);
            } catch (err) {
                setError('No se pudo cargar los logs del sistema. Es posible que no tengas los permisos necesarios.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, []);

    // Aplicar filtros
    useEffect(() => {
        let result = [...logs];

        if (filterAccion) {
            result = result.filter(log =>
                coincideBusqueda(log.accion, filterAccion)
            );
        }

        if (filterUsuario) {
            result = result.filter(log =>
                coincideBusqueda(log.usuarioEmail, filterUsuario)
            );
        }

        setFilteredLogs(result);
    }, [filterAccion, filterUsuario, logs]);

    const handleLimpiarFiltros = () => {
        setFilterAccion('');
        setFilterUsuario('');
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div>
                <p className="mt-3 fw-semibold">Cargando logs...</p>
            </div>
        );
    }

    if (error) {
        return <div className="container mt-4 alert alert-danger">{error}</div>;
    }

    return (
        <div className="area-admin">
            <div className="card border-0" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)' }}>
                <div className="card-body p-0">
                    <PageHeader
                        variante="banner"
                        icono="journal-text"
                        titulo="Visor de Logs del Sistema"
                        descripcion="Registro de auditoría y eventos del sistema"
                    />

                {/* Filtros */}
                {(() => {
                    const campoAccion = (
                        <>
                            <label className="form-label d-flex align-items-center gap-2" style={{ fontFamily: 'var(--font-family-base)' }}>
                                <i className="bi bi-lightning-charge-fill" style={{ color: '#3B82F6' }}></i>
                                Acción
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'white', border: '1px solid #e2e8f0', borderRight: 'none' }}>
                                    <i className="bi bi-funnel-fill" style={{ color: '#3B82F6' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Filtrar por acción..."
                                    value={filterAccion}
                                    onChange={(e) => setFilterAccion(e.target.value)}
                                    style={{ borderLeft: 'none', fontFamily: 'var(--font-family-base)' }}
                                />
                            </div>
                        </>
                    );
                    const campoUsuario = (
                        <>
                            <label className="form-label d-flex align-items-center gap-2" style={{ fontFamily: 'var(--font-family-base)' }}>
                                <i className="bi bi-person-fill" style={{ color: '#3B82F6' }}></i>
                                Usuario
                            </label>
                            <div className="input-group">
                                <span className="input-group-text" style={{ background: 'white', border: '1px solid #e2e8f0', borderRight: 'none' }}>
                                    <i className="bi bi-search" style={{ color: '#3B82F6' }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Filtrar por usuario..."
                                    value={filterUsuario}
                                    onChange={(e) => setFilterUsuario(e.target.value)}
                                    style={{ borderLeft: 'none', fontFamily: 'var(--font-family-base)' }}
                                />
                            </div>
                        </>
                    );

                    const chipsFiltros = [
                        filterAccion && { key: 'accion', label: filterAccion, onQuitar: () => setFilterAccion('') },
                        filterUsuario && { key: 'usuario', label: filterUsuario, onQuitar: () => setFilterUsuario('') },
                    ].filter(Boolean);

                    return esMobile ? (
                        <div className="px-4 mb-4">
                            <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                            <ChipsFiltros chips={chipsFiltros} />
                            {sheetFiltrosAbierto && (
                                <BottomSheet
                                    titulo="Filtros"
                                    onCerrar={() => setSheetFiltrosAbierto(false)}
                                    accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar', onClick: handleLimpiarFiltros } : undefined}
                                >
                                    <div className="mb-3">{campoAccion}</div>
                                    {campoUsuario}
                                </BottomSheet>
                            )}
                        </div>
                    ) : (
                        <div className="px-4 mb-4">
                            <div className="card border-0" style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.5rem' }}>
                                <div className="row g-3">
                                    <div className="col-md-5">{campoAccion}</div>
                                    <div className="col-md-5">{campoUsuario}</div>
                                    <div className="col-md-2 d-flex align-items-end">
                                        <button
                                            className="btn w-100"
                                            onClick={handleLimpiarFiltros}
                                            style={{
                                                background: '#EF4444', color: 'white', border: 'none', borderRadius: '8px',
                                                padding: '0.6rem 1rem', fontWeight: '600', fontFamily: 'var(--font-family-base)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                            }}
                                        >
                                            <i className="bi bi-x-circle-fill"></i>
                                            Limpiar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Tabla moderna */}
                <div className="px-4 pb-4">
                    <div className="table-responsive">
                        <table className="table align-middle" style={{ marginBottom: 0 }}>
                            <thead>
                            <tr style={{
                                background: 'var(--gradient-primary-light)',
                                borderBottom: '2px solid #3B82F6'
                            }}>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Fecha y Hora</th>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Usuario</th>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Acción</th>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Entidad</th>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>ID Entidad</th>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Detalle</th>
                                <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>IP</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredLogs.map(log => (
                                <tr key={log.id} style={{
                                    borderBottom: '1px solid #e5e7eb',
                                    transition: 'background-color 0.2s ease'
                                }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    <td style={{ padding: '1rem', fontFamily: 'var(--font-family-base)', color: '#475569' }}>
                                        {format(new Date(log.fecha), 'dd/MM/yyyy HH:mm:ss')}
                                    </td>
                                    <td style={{ padding: '1rem', fontWeight: '500', fontFamily: 'var(--font-family-base)' }}>
                                        {log.usuarioEmail}
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{
                                            padding: '0.4rem 0.8rem',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            // 🔹 LÓGICA DE COLORES MEJORADA
                                            background: log.accion.includes('CREATE') || log.accion.includes('INICIO') ? '#D1FAE5' : // Verdes
                                                log.accion.includes('UPDATE') || log.accion.includes('CAMBIO') ? '#DBEAFE' : // Azules
                                                    log.accion.includes('DELETE') || log.accion.includes('ELIMINA') ? '#FEE2E2' : // Rojos
                                                        log.accion.includes('LECTURA') ? '#FEF3C7' : // ⚠️ Amarillo para lectura sensible
                                                            '#F3F4F6', // Gris por defecto
                                            color: log.accion.includes('CREATE') || log.accion.includes('INICIO') ? '#065F46' :
                                                log.accion.includes('UPDATE') || log.accion.includes('CAMBIO') ? '#1E40AF' :
                                                    log.accion.includes('DELETE') || log.accion.includes('ELIMINA') ? '#DC2626' :
                                                        log.accion.includes('LECTURA') ? '#92400E' : // Marrón/Naranja oscuro
                                                            '#374151',
                                            fontFamily: 'var(--font-family-base)'
                                        }}>
                                            {log.accion}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem', fontFamily: 'var(--font-family-base)', color: '#64748b' }}>
                                        {log.entidadAfectada}
                                    </td>
                                    <td style={{ padding: '1rem', fontFamily: 'var(--font-family-base)', color: '#64748b' }}>
                                        {log.idEntidadAfectada}
                                    </td>
                                    <td
                                        style={{
                                            padding: '1rem', fontFamily: 'var(--font-family-base)',
                                            color: '#475569', fontSize: '0.85rem',
                                            maxWidth: 380, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                        }}
                                        title={log.detalle || ''}
                                    >
                                        {log.detalle || <span style={{ color: '#cbd5e1' }}>—</span>}
                                    </td>
                                    <td style={{ padding: '1rem', fontFamily: 'var(--font-family-mono)', fontSize: '0.9rem', color: '#64748b' }}>
                                        {log.ip}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
};

export default AdminLogsPage;